import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import Navigation from "@/components/Navigation";
import ProspectsKanban from "@/components/ProspectsKanban";
import AddProspectModal from "@/components/AddProspectModal";
import ProspectDetailModal from "@/components/ProspectDetailModal";
import { Search, Plus, Loader2 } from "lucide-react";

export default function Prospects() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const { data: prospects = [], isLoading } = useQuery({
    queryKey: ["prospects"],
    queryFn: async () => (await api.get("/prospects")).data,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["prospects"] });
    qc.invalidateQueries({ queryKey: ["stats"] });
  };

  const moveMutation = useMutation({
    mutationFn: ({ id, stage }) => api.patch(`/prospects/${id}/stage`, { stage }),
    onMutate: async ({ id, stage }) => {
      await qc.cancelQueries({ queryKey: ["prospects"] });
      const prev = qc.getQueryData(["prospects"]);
      qc.setQueryData(["prospects"], (old = []) => old.map((p) => (p.id === id ? { ...p, stage } : p)));
      return { prev };
    },
    onError: (e, v, ctx) => {
      qc.setQueryData(["prospects"], ctx.prev);
      toast.error("Could not move prospect");
    },
    onSettled: invalidate,
  });

  const createMutation = useMutation({
    mutationFn: (payload) => api.post("/prospects", payload),
    onSuccess: () => {
      invalidate();
      setModalOpen(false);
      toast.success("Prospect added to pipeline");
    },
    onError: () => toast.error("Failed to add prospect"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/prospects/${id}`),
    onSuccess: () => {
      invalidate();
      toast.success("Prospect removed");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => api.put(`/prospects/${id}`, payload),
    onSuccess: () => {
      invalidate();
      toast.success("Prospect updated");
    },
    onError: () => toast.error("Update failed"),
  });

  const noteMutation = useMutation({
    mutationFn: ({ id, text }) => api.post(`/prospects/${id}/activity`, { text }),
    onSuccess: () => invalidate(),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return prospects;
    return prospects.filter(
      (p) =>
        p.name?.toLowerCase().includes(q) ||
        p.company?.toLowerCase().includes(q) ||
        p.email?.toLowerCase().includes(q)
    );
  }, [prospects, search]);

  const selected = prospects.find((p) => p.id === selectedId) || null;

  return (
    <div className="gos-canvas min-h-screen">
      <Navigation />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6 fade-up">
          <div>
            <p className="eyebrow mb-2">Lead Intel</p>
            <h1 className="font-display text-3xl lg:text-4xl font-bold text-white tracking-tight">Pipeline</h1>
            <p className="text-sm text-slate-400 mt-1">Drag cards between stages to move deals along.</p>
          </div>
          <button
            data-testid="add-prospect-modal-trigger"
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-2 gos-gradient text-white font-semibold px-5 py-3 rounded-full transition-transform hover:scale-[1.03] active:scale-[0.98] self-start"
          >
            <Plus className="w-4 h-4" /> Add Prospect
          </button>
        </div>

        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              data-testid="prospect-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search prospects…"
              className="w-full bg-[#0D1322] border border-white/10 rounded-full pl-10 pr-4 py-2.5 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-indigo-500/60"
            />
          </div>
          <span className="eyebrow" data-testid="prospect-count">
            {filtered.length} prospects
          </span>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
          </div>
        ) : (
          <ProspectsKanban
            prospects={filtered}
            onMove={(id, stage) => moveMutation.mutate({ id, stage })}
            onDelete={(id) => deleteMutation.mutate(id)}
            onOpen={(p) => setSelectedId(p.id)}
          />
        )}
      </main>

      <AddProspectModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={(payload) => createMutation.mutateAsync(payload)}
      />

      <ProspectDetailModal
        prospect={selected}
        open={!!selected}
        onClose={() => setSelectedId(null)}
        onSave={(payload) => updateMutation.mutateAsync({ id: selected.id, payload })}
        onAddNote={(text) => noteMutation.mutateAsync({ id: selected.id, text })}
        onDelete={() => {
          deleteMutation.mutate(selected.id);
          setSelectedId(null);
        }}
      />
    </div>
  );
}
